/**
 * System tickets (owner, 2026-10-30): "7 of 9" plays every 7 of the 9
 * matches as its own combination; several sizes may be played at once. The
 * API settles them the same way (tottalo_api bets/combinations.go).
 */

/** A system needs at least this many matches. */
export const SYSTEM_MIN_MATCHES = 3;

/**
 * Over every combination the sizes play, the sum of the product of its
 * matches' values: for each size k the sum over all k of the matches (the
 * elementary symmetric sum). null sizes: the product of all, the ordinary
 * ticket. With the picks per match as values it counts the combinations;
 * with each match's best odds it gives the best win per unit staked.
 */
export const comboSum = (values: number[], sizes: number[] | null): number => {
  const n = values.length;
  const e = new Array<number>(n + 1).fill(0);
  e[0] = 1;
  values.forEach((v) => {
    for (let k = n; k >= 1; k--) e[k] += e[k - 1] * v;
  });
  if (!sizes) return e[n];
  return sizes.reduce((sum, k) => (k >= 1 && k <= n ? sum + e[k] : sum), 0);
};

/** The sizes a slip of `matches` matches can play: 1 .. matches - 1. */
export const systemSizesFor = (matches: number): number[] =>
  matches < SYSTEM_MIN_MATCHES
    ? []
    : Array.from({ length: matches - 1 }, (_, i) => i + 1);

/** The sizes kept when the number of matches changes. */
export const validSystem = (sizes: number[], matches: number): number[] =>
  matches < SYSTEM_MIN_MATCHES
    ? []
    : sizes.filter((k) => k >= 1 && k < matches);

/** C(n, k), for the number shown next to each size. */
export const choose = (n: number, k: number): number => {
  if (k < 0 || k > n) return 0;
  let r = 1;
  for (let i = 1; i <= k; i++) r = (r * (n - k + i)) / i;
  return Math.round(r);
};

/** "7,8/9" */
export const systemLabel = (sizes: number[], matches: number): string =>
  `${sizes.join(',')}/${matches}`;
